import type { DocPage } from "../types";

export const page: DocPage = {
  slug: "data",
  title: "Data coverage and freshness",
  navLabel: "Data coverage",
  description:
    "Understand which markets Mavori covers, how current each data family is, and how partial or missing coverage appears.",
  section: "agent-research",
  published: "2026-08-03",
  updated: "2026-08-03",
  targets: ["Mavori data coverage", "Mavori market data delay", "Mavori data sources"],
  related: ["markets", "agent", "backtesting", "account"],
  blocks: [
    {
      type: "lead",
      text: "Mavori combines market, company, filing, event, and portfolio data with source and freshness context. Coverage is explicit: a missing value stays missing, and a partial result remains marked partial wherever it is used.",
    },

    { type: "h2", text: "Supported market coverage" },
    {
      type: "table",
      caption: "Primary research coverage",
      headers: ["Data family", "Coverage"],
      rows: [
        ["Securities", "US-listed equities, ADRs, and ETFs"],
        ["Options", "Listed US equity options and their underlying securities"],
        [
          "Company data",
          "Financial statements, estimates, filings, ownership, and corporate events where available",
        ],
        [
          "Market context",
          "News, selected macroeconomic series, Treasury rates, and prediction markets",
        ],
        ["Portfolio", "Connected-broker, manual-account, and Mavori paper-account records"],
      ],
    },

    { type: "h2", text: "Freshness depends on the source" },
    {
      type: "p",
      text: "Market quotes and intraday views can be real-time or delayed depending on the feed and plan shown in the product. Option-chain snapshots and derived views can follow a different cadence. Daily price history is available after sessions are finalized, while filings, fundamentals, estimates, ownership, earnings, and macro observations update on their publishers' schedules.",
    },
    {
      type: "p",
      text: "Where timing changes the decision, Mavori shows an **As of** value or source context near the result. A current page load does not imply that every underlying fact was published at the same time.",
    },

    { type: "h2", text: "Coverage states remain visible" },
    {
      type: "dl",
      items: [
        {
          term: "Available",
          def: "The requested data is present for the stated period and scope.",
        },
        {
          term: "Partial",
          def: "Some useful data is present, but the requested scope is not complete.",
        },
        { term: "Missing", def: "The source has no usable value for the requested item." },
        {
          term: "Unavailable",
          def: "The data could not be retrieved or is outside supported coverage.",
        },
      ],
    },
    {
      type: "p",
      text: "Treat these states as part of the answer. For example, a screen can exclude companies that lack a field, and a comparison can be incomplete when one company has not reported the same period.",
    },

    { type: "h2", text: "Historical analysis has its own scope" },
    {
      type: "p",
      text: "Backtests use the history available for the chosen instruments, dates, and resolution. Their result records the assumptions and coverage for that run; current quotes or current company data are not substitutes for missing historical inputs. See [Backtesting](/docs/backtesting) before interpreting a simulated result.",
    },

    { type: "h2", text: "Use the right source for the decision" },
    {
      type: "p",
      text: "Use Mavori for research, comparison, and workflow context. Confirm time-sensitive order status, balances, and execution details with the connected broker when they must be exact. Account-specific privacy and processing information is covered in [Account and security](/docs/account).",
    },
  ],
};
